import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Redirect } from "react-router-dom";
import { injectStripe } from "react-stripe-elements";

import routes from "../../../routes";
import { validate } from "../../common/validate";
import {
  attemptGetStatus,
  attemptMakeOrderPayment
} from "../../../store/thunks/order";

import BillingAddress from "./BillingAddress";
import PaymentDetails from "./PaymentDetails";
import SenderInformation from "./SenderInformation";
import ServerAlert from "../../common/ServerAlert";

const Order = ({ stripe }) => {
  const [billingData, setBillingData] = useState({});
  const [billingErrors, setBillingErrors] = useState({});

  const [paymentData, setPaymentData] = useState({});
  const [paymentErrors, setPaymentErrors] = useState({});

  const [senderData, setSenderData] = useState({});
  const [senderErrors, setSenderErrors] = useState({});

  const [priceTag, setPriceTag] = useState(0);
  const [awaiting, setAwaiting] = useState(false);
  const [mailed, setMailed] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [submitOnce, setSubmitOnce] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [redirect, setRedirect] = useState(false);
  const [sameAddress, setSameAddress] = useState(false);

  const status = useSelector(({ order }) => order.status);
  const orderId = useSelector(({ order }) => order.data.id);
  const dispatch = useDispatch();

  useEffect(() => {
    if (orderId) {
      dispatch(attemptGetStatus(orderId)).then(res => {
        if (res.status === "PENDING_PAYMENT") {
          setPriceTag(res.detail);
        } else if (res.status === "AWAITING_MAILING") {
          setAwaiting(true);
        } else if (res.status === "MAILED") {
          setMailed(true);
        } else {
          setRedirect(true);
        }
        setInitialLoading(false);
      });
    }
  }, [orderId, status]);

  const doSubmit = async () => {
    if (paymentData.card_name) {
      const stripeResponse = await stripe.createToken({
        name: paymentData.card_name,
        address_line1: billingData.street_address,
        address_city: billingData.city,
        address_state: billingData.state,
        address_zip: billingData.zip_code,
        address_country: billingData.country
      });

      if (stripeResponse.token) {
        const returnAddress = sameAddress
          ? Object.values(billingData).join(", ")
          : Object.values(senderData).join(", ");
        await dispatch(
          attemptMakeOrderPayment(
            {
              token: stripeResponse.token.id,
              return_address: returnAddress,
              email: paymentData.email
            },
            orderId
          )
        )
          .then(() => {
            setAwaiting(true);
            setLoading(false);
          })
          .catch(error => {
            setLoading(false);
          });
      }
    }
  };

  const validateCardElements = newPaymentErrors => {
    return Promise.all(
      ["card_number", "card_expiry", "card_cvc"].map(field => {
        if (!paymentData[field]) {
          newPaymentErrors[field] = "You can't leave this field empty.";
        }
        Promise.resolve("ok");
      })
    );
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);

    setServerError(null);

    let newBillingErrors = validate(billingData, "billing_address") || {};
    let newPaymentErrors = validate(paymentData, "payment_details") || {};
    let newSenderErrors = {};
    if (!sameAddress) {
      newSenderErrors = validate(senderData, "sender_information") || {};
    }

    await validateCardElements(newPaymentErrors);

    setBillingErrors(newBillingErrors);
    setPaymentErrors({
      ...paymentErrors,
      ...newPaymentErrors
    });
    setSenderErrors(newSenderErrors);

    setSubmitOnce(true);

    if (
      Object.entries(newBillingErrors).length !== 0 ||
      Object.entries(newPaymentErrors).length !== 0 ||
      Object.entries(newSenderErrors).length !== 0
    ) {
      setLoading(false);
      return;
    }

    doSubmit();
  };

  if (initialLoading) {
    return <div className="dots dots-primary"></div>;
  }

  if (redirect) {
    return <Redirect to={routes.guestlist} />;
  }

  if (awaiting) {
    return <h1 className="text-center">Your order is being processed</h1>;
  }

  if (mailed) {
    return <h1 className="text-center">Thank you for using our services!</h1>;
  }

  return (
    <>
      <h1 className="mb-2">Finish Order</h1>
      <p className="mb-lg w-75 text-center">
        Almost done! Enter your payment details below to complete your order.
      </p>
      <BillingAddress
        data={billingData}
        setData={setBillingData}
        errors={billingErrors}
        setErrors={setBillingErrors}
        submitOnce={submitOnce}
        isChecked={sameAddress}
        setIsChecked={setSameAddress}
      />
      <PaymentDetails
        data={paymentData}
        setData={setPaymentData}
        errors={paymentErrors}
        setErrors={setPaymentErrors}
        submitOnce={submitOnce}
      />
      {!sameAddress && (
        <SenderInformation
          data={senderData}
          setData={setSenderData}
          errors={senderErrors}
          setErrors={setSenderErrors}
          submitOnce={submitOnce}
        />
      )}
      <div className="text-center mb-3">
        <h3>Total</h3>
        <p className="mb-lg">
          <span className="display-3">${priceTag}</span>USD
        </p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {!loading ? (
            <>PLACE ORDER</>
          ) : (
            <div className="dots dots-light mx-auto"></div>
          )}
        </button>
      </div>
      <ServerAlert error={serverError} />
    </>
  );
};

export default injectStripe(Order);
